// src/stations/comprehension/clueHunter.ts — KS2 English Comprehension Clue Hunter Station
// Implements Story 2.2: FR-ENG-001, FR-ENG-002, FR-ENG-003, FR-ENG-004, NFR-TOUCH-001, NFR-ADHD-001.
// Tudor reads short non-fiction marine cards (<=60 words) and taps the exact sentence
// that proves the inference claim. Clawey provides 1-to-2 sentence Socratic hints on errors.

import { tokens } from '../../ui/designTokens';
import { PassageCard, type PassageData } from '../../ui/components/passageCard';
import { soundEngine } from '../../audio/soundEngine';
import { ComprehensionStorage } from '../../storage/comprehensionStorage';
import { StationProgression } from '../../storage/stationProgression';
import type { ClaweyHud } from '../../ui/components/claweyHud';
import { SocraticTutorDrawer } from '../../ai/socraticTutorDrawer';
import { renderCornerFiligree } from '../../ui/components/ornateHelpers';
import rawPassages from '../../content/passages.json';
import { marineCards } from '../../content/marineCards';

export class ClueHunterStation {
  private passages: PassageData[];
  private passageCard: PassageCard;
  private currentAttemptCount = 0;
  private selectedSentenceIndex: number | null = null;
  private hud?: ClaweyHud;
  private streakCount = 0;
  private solvedPassageIds: Set<string>;
  private drawer?: SocraticTutorDrawer;

  constructor(hud?: ClaweyHud, initialPassageIndex = 0) {
    this.hud = hud;
    this.passages = rawPassages as PassageData[];

    const saved = ComprehensionStorage.load();
    this.streakCount = saved.streakCount;
    this.solvedPassageIds = new Set(saved.solvedPassageIds);

    this.passageCard = new PassageCard({
      passages: this.passages,
      initialIndex: initialPassageIndex,
      onSentenceClick: (sentenceIdx) => {
        soundEngine.playTap();
        this.selectedSentenceIndex = sentenceIdx;
        this.updateSubmitButtonState();
      },
      onPassageChange: () => {
        soundEngine.playTap();
        this.currentAttemptCount = 0;
        this.selectedSentenceIndex = null;
        this.clearFeedback();
      },
    });
  }

  setHud(hud: ClaweyHud): void {
    this.hud = hud;
  }

  /** Render the complete Clue Hunter station */
  render(): HTMLElement {
    const station = document.createElement('section');
    station.id = 'clue-hunter-station';
    station.className = 'station-container';
    station.style.padding = '0 0 40px 0';

    station.innerHTML = `
      ${this.renderHeader()}
      ${this.renderClaimBanner()}
      <div id="passage-card-mount-point" style="margin-bottom: 24px;"></div>
      ${this.renderActionBar()}
      <div id="clue-feedback-area" style="min-height: 20px; margin-bottom: 24px;"></div>
      ${this.renderPassageGrid()}
    `;

    // Mount PassageCard DOM component
    const mountPoint = station.querySelector<HTMLElement>('#passage-card-mount-point');
    if (mountPoint) {
      mountPoint.appendChild(this.passageCard.render());
    }

    this.attachEventListeners(station);

    return station;
  }

  // ─── Header ──────────────────────────────────────

  private renderHeader(): string {
    const isMuted = soundEngine.isMuted();

    return `
      <div class="vellum-surface morris-card morris-card-gold" style="margin-bottom: 14px; padding: 16px 20px;">
        ${renderCornerFiligree()}
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; position: relative; z-index: 2;">
          <div style="flex: 1; min-width: 240px;">
            <div class="atelier-eyebrow-pill" style="margin-bottom: 6px; padding: 3px 10px; font-size: 0.72rem;">
              <span>🔍</span> KS2 Reading Domain 2d · Proof &amp; Inference
            </div>
            <h2 class="hero-title" style="font-size: 1.65rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 4px 0;">
              Clue Hunter: Evidence Finder
            </h2>
            <p style="font-size: 0.92rem; color: #264063; max-width: 580px; line-height: 1.45; margin: 0;">
              <strong>Tap the exact sentence</strong> in the field card that proves the claim, then submit your proof!
            </p>
          </div>

          <div style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap;">
            <!-- Streaks & solved badges -->
            <div style="text-align: center; padding: 6px 14px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 10px; box-shadow: 0 2px 6px rgba(10, 25, 47, 0.08);">
              <div style="font-size: 0.65rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Proof Streak</div>
              <div id="clue-streak-counter" style="font-family: ${tokens.font.mono}; font-size: 1.5rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; line-height: 1.1;">
                ${this.streakCount}
              </div>
            </div>

            <div style="text-align: center; padding: 6px 14px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.seaweedSage}; border-radius: 10px; box-shadow: 0 2px 6px rgba(10, 25, 47, 0.08);">
              <div style="font-size: 0.65rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase;">Clues Solved</div>
              <div id="clue-solved-counter" style="font-family: ${tokens.font.mono}; font-size: 1.5rem; font-weight: 800; color: ${tokens.color.seaweedSage}; line-height: 1.1;">
                ${this.solvedPassageIds.size} / ${this.passages.length}
              </div>
            </div>

            <button 
              id="btn-clue-sound-toggle" 
              class="touch-target btn-atelier" 
              style="padding: 6px 12px; border-radius: 16px; font-size: 0.82rem; font-weight: 800; min-height: 40px;"
            >
              ${isMuted ? '🔇 Off' : '🔊 Sound'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ─── Claim Banner ────────────────────────────────

  private renderClaimBanner(): string {
    const current = this.passageCard.currentPassage;
    const claim = current.inferenceClaim || 'Find the sentence that best supports the naturalist claim above.';

    return `
      <div 
        id="clue-claim-banner" 
        style="
          margin-bottom: 16px;
          padding: 12px 18px;
          background: linear-gradient(135deg, rgba(255, 248, 235, 0.98) 0%, rgba(254, 237, 212, 0.95) 100%);
          border: 1.5px solid var(--gilded-brass);
          border-left: 5px solid var(--madder-coral);
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(10, 25, 47, 0.08);
          display: flex;
          align-items: center;
          gap: 14px;
        "
      >
        <div style="
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: rgba(255, 107, 74, 0.12);
          border: 1px solid rgba(255, 107, 74, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          flex-shrink: 0;
        ">🎯</div>
        <div style="flex: 1;">
          <div style="font-size: 0.72rem; text-transform: uppercase; font-weight: 800; color: ${tokens.color.madderCoral}; letter-spacing: 0.08em; margin-bottom: 2px;">
            Target Inference to Prove
          </div>
          <div id="active-inference-claim" style="font-size: 1.1rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; line-height: 1.35;">
            "${claim}"
          </div>
        </div>
      </div>
    `;
  }

  // ─── Action Bar (Submit Proof Button) ────────────

  private renderActionBar(): string {
    return `
      <div 
        class="action-bar" 
        style="
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        "
      >
        <button
          id="btn-submit-clue"
          class="touch-target btn-primary"
          style="
            min-width: 240px;
            padding: 14px 28px;
            font-size: 1.1rem;
            font-weight: 800;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            opacity: 0.5;
            cursor: not-allowed;
            transition: all 0.2s ease;
          "
          disabled
        >
          <span>🔍</span> Submit Selected Proof
        </button>

        <button
          id="btn-ask-hint"
          class="touch-target btn-secondary"
          style="
            padding: 12px 20px;
            font-size: 0.95rem;
            font-weight: 800;
            display: inline-flex;
            align-items: center;
            gap: 6px;
          "
        >
          <span>💡</span> Clawey's Clue
        </button>
      </div>
    `;
  }

  // ─── Passage Grid Selector ───────────────────────

  private renderPassageGrid(): string {
    return `
      <div class="vellum-surface morris-card marine-cards-section" style="padding: 24px 20px; margin-top: 28px; position: relative;">
        ${renderCornerFiligree()}
        <div style="display: flex; justify-content: center; align-items: center; margin-bottom: 20px; position: relative; z-index: 2;">
          <img 
            src="/assets/cards/marine-cards-title-badge.png" 
            alt="All 10 Marine Ecology Field Cards" 
            class="marine-cards-title-badge"
            style="max-width: 380px; width: 100%; height: auto; filter: drop-shadow(0 4px 12px rgba(10,25,47,0.3));" 
          />
        </div>
        <div class="marine-cards-grid" style="position: relative; z-index: 2;">
          ${this.passages.map((p, idx) => {
            const card = marineCards[idx] || {
              id: idx + 1,
              title: `Card ${idx + 1}`,
              subtitle: p.cardSubtitle || 'Marine Life',
              image: p.cardImage || `/assets/cards/card-${idx + 1}-lobster.png`,
              glowColor: p.cardGlowColor || '#00E5FF'
            };
            const isSolved = this.solvedPassageIds.has(p.id);
            const isCurrent = idx === this.passageCard.activeIndex;
            return `
              <button
                class="touch-target passage-selector-btn marine-card-btn ${isCurrent ? 'active-card' : ''} ${isSolved ? 'solved-card' : ''}"
                data-passage-index="${idx}"
                style="--card-glow: ${card.glowColor};"
                aria-label="${card.title} ${card.subtitle}: ${p.title}"
              >
                <div class="marine-card-inner">
                  <img 
                    src="${card.image}" 
                    alt="${card.title} - ${card.subtitle}" 
                    class="marine-card-img"
                    loading="lazy"
                  />
                  <div class="marine-card-glow-overlay"></div>
                  ${isSolved ? `<span class="marine-card-solved-badge" title="Evidence Solved">✓ Solved</span>` : ''}
                  <div class="marine-card-caption">
                    <span class="marine-card-num">${card.title}</span>
                    <span class="marine-card-sub">${card.subtitle}</span>
                  </div>
                </div>
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  // ─── Event Listeners ─────────────────────────────

  private attachEventListeners(container: HTMLElement): void {
    // Sound toggle
    const soundToggle = container.querySelector<HTMLButtonElement>('#btn-clue-sound-toggle');
    soundToggle?.addEventListener('click', () => {
      soundEngine.playTap();
      const isMuted = soundEngine.toggleMute();
      soundToggle.textContent = isMuted ? '🔇 Off' : '🔊 Sound';
    });

    // Submit Proof button
    const submitBtn = container.querySelector<HTMLButtonElement>('#btn-submit-clue');
    submitBtn?.addEventListener('click', () => {
      if (this.selectedSentenceIndex !== null) {
        this.verifyProofSelection(container);
      }
    });

    // Ask Hint button
    const hintBtn = container.querySelector<HTMLButtonElement>('#btn-ask-hint');
    hintBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.giveSocraticHint(container);
    });

    // Passage selector grid
    container.querySelectorAll<HTMLButtonElement>('.passage-selector-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        soundEngine.playTap();
        const idx = parseInt(btn.dataset.passageIndex ?? '0', 10);
        this.passageCard.goTo(idx);
        this.refreshActivePassage(container);
      });
    });
  }

  // ─── Interaction & Verification Logic ────────────

  private updateSubmitButtonState(): void {
    const submitBtn = document.querySelector<HTMLButtonElement>('#btn-submit-clue');
    if (submitBtn) {
      const hasSelection = this.selectedSentenceIndex !== null;
      submitBtn.disabled = !hasSelection;
      submitBtn.style.opacity = hasSelection ? '1' : '0.5';
      submitBtn.style.cursor = hasSelection ? 'pointer' : 'not-allowed';
      if (hasSelection) {
        submitBtn.style.boxShadow = `0 4px 14px rgba(0, 229, 255, 0.4)`;
      } else {
        submitBtn.style.boxShadow = 'none';
      }
    }
  }

  private verifyProofSelection(container: HTMLElement): void {
    const current = this.passageCard.currentPassage;
    const correctIndex = current.correctSentenceIndex ?? 1;
    const isCorrect = this.selectedSentenceIndex === correctIndex;

    this.currentAttemptCount += 1;

    // Log attempt to local storage
    ComprehensionStorage.recordAttempt({
      passageId: current.id,
      selectedSentenceIndex: this.selectedSentenceIndex ?? 0,
      correct: isCorrect,
      attemptNumber: this.currentAttemptCount,
      timestamp: Date.now(),
    });

    const feedbackArea = container.querySelector<HTMLElement>('#clue-feedback-area');
    if (!feedbackArea) return;

    if (isCorrect) {
      this.handleCorrectProof(container, current, feedbackArea);
    } else {
      this.handleIncorrectProof(container, current, feedbackArea);
    }
  }

  private handleCorrectProof(container: HTMLElement, passage: PassageData, feedbackArea: HTMLElement): void {
    this.streakCount += 1;
    this.solvedPassageIds.add(passage.id);
    StationProgression.completeSet('comprehension', this.passageCard.activeIndex + 1, 1, 1);

    // Audio trigger: victory chime or streak fanfare
    if (this.streakCount >= 3) {
      soundEngine.playStreak();
    } else {
      soundEngine.playSuccess();
    }

    // Clawey mascot celebration
    if (this.hud) {
      const celebrationMessages = [
        `Splendid proof, Tudor! That exact sentence proves our claim!`,
        `By Neptune's claw! You pinpointed the evidence like a true Master Naturalist!`,
        `Spot on deduction! The text evidence supports our claim without a doubt!`,
      ];
      const message = celebrationMessages[Math.floor(Math.random() * celebrationMessages.length)]!;
      this.hud.say(message, 'celebrating');
    }

    // Update streak counter in DOM
    const streakEl = container.querySelector<HTMLElement>('#clue-streak-counter');
    if (streakEl) streakEl.textContent = String(this.streakCount);

    const solvedEl = container.querySelector<HTMLElement>('#clue-solved-counter');
    if (solvedEl) solvedEl.textContent = `${this.solvedPassageIds.size} / ${this.passages.length}`;

    // Mark current card as solved in grid
    const activeBtn = container.querySelector<HTMLButtonElement>(`.passage-selector-btn[data-passage-index="${this.passageCard.activeIndex}"]`);
    if (activeBtn) {
      activeBtn.classList.add('solved-card');
      const inner = activeBtn.querySelector('.marine-card-inner');
      if (inner && !inner.querySelector('.marine-card-solved-badge')) {
        const badge = document.createElement('span');
        badge.className = 'marine-card-solved-badge';
        badge.title = 'Evidence Solved';
        badge.textContent = '✓ Solved';
        inner.appendChild(badge);
      }
    }

    // Render positive feedback banner
    feedbackArea.innerHTML = `
      <div 
        class="morris-card" 
        style="
          padding: 20px 24px;
          border-left: 5px solid ${tokens.color.seaweedSage};
          background: rgba(61, 90, 69, 0.08);
          border-radius: 12px;
          display: flex;
          align-items: flex-start;
          gap: 14px;
        "
      >
        <span style="font-size: 2.2rem;">🌟</span>
        <div style="flex: 1;">
          <h4 style="font-size: 1.15rem; color: ${tokens.color.seaweedSage}; margin-bottom: 4px;">
            Evidence Verified! Clue Unlocked!
          </h4>
          <p style="font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; line-height: 1.6; margin-bottom: 12px;">
            "${passage.sentences[passage.correctSentenceIndex ?? 1]}" explicitly proves the target claim!
          </p>
          <div style="display: flex; gap: 10px; flex-wrap: wrap;">
            <button 
              id="btn-next-clue-card" 
              class="touch-target btn-gold" 
              style="padding: 10px 20px; font-size: 0.95rem; font-weight: 800;"
            >
              Next Field Card →
            </button>
            ${passage.vocabularyQuestion ? `
              <button 
                id="btn-view-vocab-check" 
                class="touch-target btn-secondary" 
                style="padding: 10px 18px; font-size: 0.9rem;"
              >
                Explore Vocabulary Question 📖
              </button>
            ` : ''}
          </div>
        </div>
      </div>
    `;

    feedbackArea.querySelector('#btn-next-clue-card')?.addEventListener('click', () => {
      soundEngine.playTap();
      this.passageCard.next();
      this.refreshActivePassage(container);
    });

    feedbackArea.querySelector('#btn-view-vocab-check')?.addEventListener('click', () => {
      soundEngine.playTap();
      this.showVocabularyDrawer(container, passage);
    });
  }

  private handleIncorrectProof(container: HTMLElement, passage: PassageData, feedbackArea: HTMLElement): void {
    this.streakCount = 0;
    soundEngine.playTryAgain();

    // Clawey Socratic Hint (≤2 sentences, strictly supportive)
    const hint = passage.socraticHint || "Look closely at which sentence directly mentions the target action. What is the author showing here?";
    if (this.hud) {
      this.hud.say(hint, 'thinking');
    }

    // Update streak counter in DOM
    const streakEl = container.querySelector<HTMLElement>('#clue-streak-counter');
    if (streakEl) streakEl.textContent = '0';

    feedbackArea.innerHTML = `
      <div 
        class="morris-card" 
        style="
          padding: 18px 22px;
          border-left: 5px solid ${tokens.color.madderCoral};
          background: rgba(255, 107, 74, 0.06);
          border-radius: 12px;
          display: flex;
          align-items: flex-start;
          gap: 14px;
        "
      >
        <span style="font-size: 2rem;">💡</span>
        <div style="flex: 1;">
          <h4 style="font-size: 1.05rem; color: ${tokens.color.madderCoral}; margin-bottom: 4px;">
            An Honourable Try! Let's Refine the Clue
          </h4>
          <p style="font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; line-height: 1.6; margin-bottom: 8px;">
            ${hint}
          </p>
          <div style="font-size: 0.85rem; color: #6B7C93; font-style: italic;">
            Tap another sentence in the card above and check again.
          </div>
        </div>
      </div>
    `;
  }

  private giveSocraticHint(container: HTMLElement): void {
    const current = this.passageCard.currentPassage;
    const hint = current.socraticHint || "Notice the sentence that talks about the creature's protective mechanism, Tudor.";
    if (this.hud) {
      this.hud.say(hint, 'thinking');
    }

    if (!this.drawer) {
      this.drawer = new SocraticTutorDrawer();
      container.appendChild(this.drawer.getElement());
    }
    this.drawer.openWithRequest({
      questionId: current.id,
      domain: 'comprehension',
      topic: 'Inference',
      prompt: current.inferenceClaim || current.title,
      contextText: current.sentences.join(' '),
      attemptCount: this.currentAttemptCount,
    });

    const feedbackArea = container.querySelector<HTMLElement>('#clue-feedback-area');
    if (feedbackArea) {
      feedbackArea.innerHTML = `
        <div 
          class="morris-card" 
          style="
            padding: 16px 20px;
            border-left: 4px solid ${tokens.color.gildedBrass};
            background: rgba(255, 246, 229, 0.8);
            border-radius: 10px;
            display: flex;
            align-items: center;
            gap: 12px;
          "
        >
          <span style="font-size: 1.8rem;">🦞</span>
          <p style="font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; margin: 0; line-height: 1.5;">
            <strong>Clawey's Socratic Clue:</strong> ${hint}
          </p>
        </div>
      `;
    }
  }

  private showVocabularyDrawer(container: HTMLElement, passage: PassageData): void {
    const vocab = passage.vocabularyQuestion;
    if (!vocab) return;

    const feedbackArea = container.querySelector<HTMLElement>('#clue-feedback-area');
    if (!feedbackArea) return;

    feedbackArea.innerHTML = `
      <div 
        class="morris-card" 
        style="
          padding: 22px 24px;
          border: 2px solid ${tokens.color.gildedBrass};
          background: rgba(255, 253, 249, 0.98);
          border-radius: 14px;
        "
      >
        <div style="font-size: 0.75rem; text-transform: uppercase; font-weight: 800; color: ${tokens.color.madderCoral}; margin-bottom: 8px;">
          📖 Vocabulary Context Check (Domain 2a)
        </div>
        <h4 style="font-size: 1.15rem; color: ${tokens.color.morrisIndigo}; margin-bottom: 14px;">
          ${vocab.question}
        </h4>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px;">
          ${vocab.options.map((opt, i) => `
            <button
              class="touch-target vocab-option-btn"
              data-opt-index="${i}"
              style="
                padding: 12px 14px;
                border-radius: 8px;
                border: 1.5px solid rgba(26, 54, 93, 0.2);
                background: #fff;
                color: ${tokens.color.morrisIndigo};
                font-weight: 700;
                font-size: 0.95rem;
                cursor: pointer;
                text-align: left;
                transition: all 0.15s ease;
              "
            >
              ${['A', 'B', 'C', 'D'][i]}) ${opt}
            </button>
          `).join('')}
        </div>
        <div id="vocab-result" style="display: none; padding: 12px 16px; border-radius: 8px;"></div>
      </div>
    `;

    feedbackArea.querySelectorAll<HTMLButtonElement>('.vocab-option-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        soundEngine.playTap();
        const selectedOpt = parseInt(btn.dataset.optIndex ?? '-1', 10);
        const isRight = selectedOpt === vocab.answerIndex;
        const resultEl = feedbackArea.querySelector<HTMLElement>('#vocab-result');

        if (resultEl) {
          resultEl.style.display = 'block';
          if (isRight) {
            soundEngine.playSuccess();
            resultEl.style.background = 'rgba(61, 90, 69, 0.1)';
            resultEl.style.border = `1px solid ${tokens.color.seaweedSage}`;
            resultEl.innerHTML = `
              <strong style="color: ${tokens.color.seaweedSage};">✓ Correct!</strong>
              <p style="font-size: 0.9rem; margin: 4px 0 0 0; color: ${tokens.color.morrisIndigo};">${vocab.explanation}</p>
            `;
          } else {
            soundEngine.playTryAgain();
            resultEl.style.background = 'rgba(255, 107, 74, 0.1)';
            resultEl.style.border = `1px solid ${tokens.color.madderCoral}`;
            resultEl.innerHTML = `
              <strong style="color: ${tokens.color.madderCoral};">Almost!</strong>
              <p style="font-size: 0.9rem; margin: 4px 0 0 0; color: ${tokens.color.morrisIndigo};">${vocab.explanation}</p>
            `;
          }
        }
      });
    });
  }

  private clearFeedback(): void {
    const feedbackArea = document.querySelector<HTMLElement>('#clue-feedback-area');
    if (feedbackArea) feedbackArea.innerHTML = '';
  }

  private refreshActivePassage(container: HTMLElement): void {
    this.clearFeedback();
    this.selectedSentenceIndex = null;
    this.updateSubmitButtonState();

    // Update active inference claim banner
    const claimEl = container.querySelector<HTMLElement>('#active-inference-claim');
    if (claimEl) {
      claimEl.textContent = `"${this.passageCard.currentPassage.inferenceClaim || 'Find the proof sentence.'}"`;
    }

    // Refresh grid button highlights
    container.querySelectorAll<HTMLButtonElement>('.passage-selector-btn').forEach(btn => {
      const idx = parseInt(btn.dataset.passageIndex ?? '-1', 10);
      const isCurrent = idx === this.passageCard.activeIndex;
      btn.classList.toggle('active-card', isCurrent);
    });
  }
}
